import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { parseMentions } from '../../utils/mentionParser';

/**
 * Calculates the best text color (black or white) for a given background color
 * @param {string} backgroundColor - Hex color string (e.g., '#ff5733' or 'rgb(255, 87, 51)')
 * @returns {string} Either 'black' or 'white'
 */
function getContrastTextColor(backgroundColor) {
  if (!backgroundColor) return 'white';
  
  // Convert to RGB values
  let r, g, b;
  
  if (backgroundColor.startsWith('#')) {
    // Handle hex colors
    const hex = backgroundColor.replace('#', '');
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else if (backgroundColor.startsWith('rgb')) {
    // Handle rgb/rgba colors
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    } else {
      return 'white'; // fallback
    }
  } else {
    return 'white'; // fallback for unknown formats
  }
  
  // Calculate relative luminance using the formula from WCAG
  // https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black text for bright backgrounds, white text for dark backgrounds
  return luminance > 0.5 ? 'black' : 'white';
}

const MentionHighlight = styled.span`
  background-color: ${props => {
    const color = props.$color || 'var(--clr-purple)';
    // Convert any color format to rgba with 20% opacity
    if (color.startsWith('#')) {
      // Convert hex to rgba
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, 0.8)`;
    } else if (color.startsWith('rgb(')) {
      // Convert rgb to rgba by replacing rgb with rgba and adding alpha
      return color.replace('rgb(', 'rgba(').replace(')', ', 0.8)');
    } else if (color.startsWith('rgba(')) {
      // If already rgba, extract RGB values and set alpha to 0.8
      const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (matches) {
        return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, 0.8)`;
      }
    }
    // Fallback for CSS variables or other formats
    return color;
  }};
  color: ${props => props.$textColor || 'white'};
  padding: 2px 4px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => {
      const color = props.$color || 'var(--clr-purple)';
      // Slightly more opaque on hover (30% instead of 20%)
      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.9)`;
      } else if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', ', 0.9)');
      } else if (color.startsWith('rgba(')) {
        const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (matches) {
          return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, 0.9)`;
        }
      }
      return color;
    }};
  }
`;

const MessageText = styled.span`
  margin: 0;
  color: ${(props) => props.$color || 'var(--clr-sec)'};
  font-family: ${(props) => {
    if (props.$font === 'Caveat') {
      return "'Caveat', cursive";
    } else if (props.$font === 'JetBrains Mono') {
      return "'JetBrains Mono', 'Courier New', monospace";
    } else if (props.$font === 'Inter') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else if (props.$font === 'Roboto') {
      return "'Roboto', system-ui, -apple-system, sans-serif";
    } else if (props.$font === 'Times New Roman') {
      return "'Times New Roman', Georgia, serif";
    } else if (props.$font === 'Inter Bold Small-Caps') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else {
      return 'inherit';
    }
  }};
  ${(props) => props.$font === 'Inter Bold Small-Caps' ? `
    font-variant: small-caps;
    font-weight: bold;
  ` : `
    font-weight: ${props.$bold ? '600' : 'normal'};
  `}
  font-size: ${(props) => {
    if (props.$font === 'Caveat') {
      return '18px';
    } else {
      return '14px';
    }
  }};
  line-height: 1.4;

  @media (min-width: 768px) {
    font-size: ${(props) => {
      if (props.$font === 'Caveat') {
        return '22px';
      } else {
        return '16px';
      }
    }};
  }
`;

function MessageWithMentions({ 
  messageText, 
  color, 
  font, 
  bold, 
  onMentionClick 
}) {
  const messages = useSelector((state) => Object.values(state.messages.byId));
  
  const parsedMessage = parseMentions(messageText, messages);
  
  const handleMentionClick = (mentionData) => {
    if (onMentionClick) {
      onMentionClick(mentionData);
    }
  };

  return (
    <MessageText $color={color} $font={font} $bold={bold}>
      {parsedMessage.map((part, index) => {
        if (part.type === 'mention') {
          const textColor = getContrastTextColor(part.color);
          return (
            <MentionHighlight
              key={index}
              $color={part.color}
              $textColor={textColor}
              onClick={() => handleMentionClick(part)}
              title={`Mentioned user: ${part.nickname}`}
            >
              {part.content}
            </MentionHighlight>
          );
        } else {
          return part.content;
        }
      })}
    </MessageText>
  );
}

export default MessageWithMentions;